import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Label,
  LoadingIndicator,
  MultiSelect,
  useToggle,
} from '@commenda/kakashi';
import { useState } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { BaseAssignee, Filing } from '../../molecules';

interface AddAssigneeModalProps {
  isOpen: boolean;
  onClose: () => void;
  filing: Filing;
  availableAssignees: BaseAssignee[];
  onAddAssignees: (assigneeIds: string[]) => Promise<boolean>;
  onSuccess: () => void;
  allowMultipleAssignees?: boolean;
}

const AddAssigneeModal = ({
  isOpen,
  onClose,
  filing,
  availableAssignees,
  onAddAssignees,
  onSuccess,
  allowMultipleAssignees = true,
}: AddAssigneeModalProps) => {
  const { setNotification } = useNotification();
  const isAddingAssignees = useToggle();
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);

  const onAssigneeChange = (assigneeIds: string[]) => {
    setSelectedAssignees(
      allowMultipleAssignees
        ? assigneeIds
        : assigneeIds.length > 0
          ? [assigneeIds[assigneeIds.length - 1]]
          : []
    );
  };

  const members = availableAssignees.map((member) => ({
    value: member.id,
    label: member.name,
  }));

  const onSubmit = async () => {
    isAddingAssignees.on();
    const success = await onAddAssignees(selectedAssignees);

    if (!success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to add assignees',
      });
      isAddingAssignees.off();
      return;
    }

    isAddingAssignees.off();
    onSuccess();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col gap-xs items-start border-b-0">
          <DialogTitle>Add assignees</DialogTitle>
          <DialogDescription>
            You are adding assignees to&nbsp;
            <span className="font-bold">{filing.filingName}</span> for{' '}
            {filing.corporation.companyDisplayName}.
          </DialogDescription>
        </DialogHeader>
        <div className="p-3xl">
          <FieldWrapper>
            <Label htmlFor="assignees">Assignees</Label>
            <MultiSelect
              id="assignees"
              placeholder="Select assignees"
              onValueChange={onAssigneeChange}
              options={members}
              defaultValue={selectedAssignees}
              hideSelectAll
            />
          </FieldWrapper>
        </div>
        <DialogFooter className="border-t-0">
          <Button size="sm" variant="secondary" onClick={onClose}>
            Back
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={onSubmit}
            disabled={isAddingAssignees.value}
          >
            {isAddingAssignees.value ? <LoadingIndicator size={16} /> : null}
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { AddAssigneeModal };
